<template>
  <div class="photo-booth modal panel panel-top" @click.self="cancel">
    <div class="panel-container is-animated slide-from-top">
      <div v-if="uploading">
        <header class="panel-header" />
        <div class="panel-body" :style="{ height: canvasHeight + 'px' }">
          <div class="panel-interstitial">
            <interstitial-uploader
              :file="file"
              :download="true"
              :title="__('Saving photo')"
              @done="onUploadDone"
              @cancel="onUploadCancel"
            />
          </div>
        </div>
        <footer class="panel-footer" />
      </div>
      <div v-else>
        <header class="panel-header">
          <div class="panel-left-item">
            <button class="text-button narrow-button" @click="cancel">{{ __('Close') }}</button>
          </div>
          <div class="panel-title">{{ __('Photo booth') }}</div>
          <div class="panel-right-item">
            <button v-if="captured" class="flat-button narrow-button" @click="save">{{ __('Save') }}</button>
          </div>
        </header>
        <div class="panel-body" :style="{ height: canvasHeight + 'px' }">
          <div v-if="!streaming" class="panel-interstitial">
            <div class="tv">
              <div class="tv-cell">
                <OzLoadingSpinner />
              </div>
              <div class="tv-cell">
                <p>{{ __('Starting camera ...') }}</p>
                <p class="caption-type">
                  {{ __('Please make sure you have given us permission to use your camera.') }}
                </p>
              </div>
            </div>
          </div>
          <video
            ref="player"
            :class="{ 'out-of-bounds': captured || !streaming }"
            :width="canvasWidth"
            :height="canvasHeight"
            autoplay
          />
          <canvas ref="canvas" :class="{ 'out-of-bounds': !captured }" :width="canvasWidth" :height="canvasHeight" />
          <canvas
            ref="hiddenCanvas"
            :class="{ 'out-of-bounds': true }"
            :width="hiddenCanvasWidth"
            :height="hiddenCanvasHeight"
          />
        </div>
        <footer class="panel-footer">
          <button
            v-show="streaming && !captured && secondsToCapture === 0"
            class="floating-action-button shadow-z2 hover-hover:hover:shadow-z3"
            :title="__('Capture')"
            @click="captureInThree"
          >
            <base-icon name="camera" />
          </button>
          <button v-show="captured" class="floating-action-button photo-booth-redo" :title="__('Redo')" @click="redo">
            <base-icon name="refresh" />
          </button>
          <div v-show="secondsToCapture > 0" class="photo-booth-countdown notice-scheme">
            <div class="is-animated scale-up">{{ secondsToCapture }}</div>
          </div>
          <div v-if="!captured && videoDevices.length > 1" class="photo-booth-video-chooser">
            <div class="select-box">
              <select @change="chooseVideoDevice">
                <option
                  v-for="videoDevice in videoDevices"
                  :key="videoDevice.id"
                  :selected="videoDevice.id == activeVideoDeviceId"
                  :value="videoDevice.id"
                >
                  {{ videoDevice.label }}
                </option>
              </select>
            </div>
          </div>
          <div v-if="captured" class="photo-booth-caption-form">
            <input
              ref="captionInput"
              v-model="title"
              type="text"
              autofocus
              :placeholder="__('Enter a caption (optional)')"
              @keyup.enter="save"
            />
          </div>
          <div v-if="captured && filtersEnabled" class="photo-booth-filter-form">
            <div class="radio-button">
              <label
                ><input type="radio" :checked="filter == 'normal'" value="normal" @change="applyFilter" /><span>
                  {{ __('Normal') }}
                </span></label
              >
              <label
                ><input type="radio" :checked="filter == 'grayscale'" value="grayscale" @change="applyFilter" /><span>
                  {{ __('Grayscale') }}
                </span></label
              >
              <label
                ><input type="radio" :checked="filter == 'sepia'" value="sepia" @change="applyFilter" /><span>
                  {{ __('Sepia') }}
                </span></label
              >
            </div>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { trackEvent } from 'bits/analytics'
import { shutterSound } from 'bits/sounds'
import OzLoadingSpinner from 'library/v4/components/OzLoadingSpinner.vue'
import BaseIcon from 'vuecomponents/base_icon'
import AVRecorderMixin from 'vuemixins/av_recorder_mixin'
import ContentSourceUploaderMixin from 'vuemixins/content_source_uploader_mixin'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'

export default {
  components: { BaseIcon, OzLoadingSpinner },
  mixins: [ContentSourceUploaderMixin, AVRecorderMixin, EscapeToCancelMixin],
  data() {
    return {
      canvasWidth: 640,
      canvasHeight: 360,
      filter: 'normal',
      filtersEnabled: false,
      hiddenCanvasWidth: 640,
      hiddenCanvasHeight: 360,
      captured: false,
      secondsToCapture: 0,
      title: '',
    }
  },
  computed: {
    constraints() {
      return {
        video: {
          deviceId: this.activeVideoDeviceId ? { exact: this.activeVideoDeviceId } : undefined,
          width: 3840,
          height: 2160,
          // Use landscape camera
          aspectRatio: 16 / 9,
        },
      }
    },
  },
  mounted() {
    this.mountStreaming()
  },
  beforeDestroy() {
    this.stopStreaming()
  },
  methods: {
    applyFilter(e) {
      this.filter = e.target.value
      this.draw(false)
      trackEvent('Photo Booth', 'Applied Filter', this.filter)
    },
    cancel() {
      if (!this.captured) {
        this.close()
      } else if (confirm(this.__('You have not saved your photo. Are you sure you want to close photo booth?'))) {
        this.close()
      }
    },
    close() {
      this.$emit('close')
    },
    captureInThree() {
      this.secondsToCapture = 3
      setTimeout(() => {
        this.secondsToCapture = 2
      }, 1000)
      setTimeout(() => {
        this.secondsToCapture = 1
      }, 2000)
      setTimeout(() => {
        this.secondsToCapture = 0
        this.capture()
      }, 3000)
    },
    capture() {
      trackEvent('Photo Booth', 'Captured')
      this.playSound()
      this.captured = true
      this.draw(true)
      this.$nextTick(() => {
        this.$refs.captionInput.focus()
      })
    },
    draw(newCapture = true) {
      const canvas = this.$refs.canvas
      const player = this.$refs.player
      const context = canvas.getContext('2d')
      const hiddenCanvas = this.$refs.hiddenCanvas
      const hiddenContext = hiddenCanvas.getContext('2d')
      if (context.filter) {
        this.filtersEnabled = true
        if (this.filter === 'normal') {
          context.filter = 'none'
          hiddenContext.filter = 'none'
        } else if (this.filter === 'sepia') {
          context.filter = 'sepia(100%)'
          hiddenContext.filter = 'sepia(100%)'
        } else if (this.filter === 'grayscale') {
          context.filter = 'grayscale(100%)'
          hiddenContext.filter = 'grayscale(100%)'
        }
      }
      if (newCapture) {
        context.drawImage(player, 0, 0, canvas.width, canvas.height)
        hiddenContext.drawImage(player, 0, 0, hiddenCanvas.width, hiddenCanvas.height)
      } else {
        context.drawImage(hiddenCanvas, 0, 0, canvas.width, canvas.height)
      }
    },
    gotStream(stream) {
      const videoTrack = stream.getVideoTracks()[0]
      // not supported properly by many browsers, e.g. Safari
      if (typeof videoTrack.getSettings === 'function') {
        const settings = videoTrack.getSettings()
        if (settings.height) {
          this.hiddenCanvasHeight = settings.height
        }
        if (settings.width) {
          this.hiddenCanvasWidth = settings.width
        }
        if (settings.aspectRatio) {
          this.canvasHeight = Math.floor(this.canvasWidth / settings.aspectRatio)
        }
      }
      this.$refs.player.srcObject = stream
    },
    playSound() {
      shutterSound.play()
    },
    redo() {
      trackEvent('Photo Booth', 'Redo')
      this.captured = false
    },
    save() {
      trackEvent('Photo Booth', 'Saved')
      const hiddenCanvas = this.$refs.hiddenCanvas
      const caption = this.$refs.captionInput.value
      // Just because we add a filter to a canvas doesn't mean the underlying image has the filter.
      // So we need to make sure the underlying image is with the filter.
      if (this.filter !== 'normal') {
        const hiddenContext = hiddenCanvas.getContext('2d')
        hiddenContext.drawImage(hiddenCanvas, 0, 0, hiddenCanvas.width, hiddenCanvas.height)
      }
      this.handleCaption(caption)
      if (hiddenCanvas.msToBlob) {
        const blob = hiddenCanvas.msToBlob('image/jpeg')
        blob.name = 'drawing.png'
        this.handleFile(blob)
      } else {
        hiddenCanvas.toBlob((blob) => {
          const fileName = (this.title || 'photo') + '.jpeg'
          // This fails in safari, so don't do it.
          // Instead, set a "name" property on blob.
          // const file = new File([blob], `${fileName}.png`)
          blob.name = fileName
          this.handleFile(blob)
        }, 'image/jpeg')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
canvas,
video {
  display: block;
}
.panel-body {
  transition: height 0.2s;
}
.out-of-bounds {
  position: absolute;
  top: -5000px;
  left: -5000px;
}
.photo-booth-redo {
  background-color: $grey-2;
  color: $caption-text-color;

  @include button-hover-active {
    background-color: $grey-4;
    color: $body-text-color;
  }
}
.photo-booth-countdown {
  width: 56px;
  height: 56px;
  border-radius: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: $headline-font-size;
  font-weight: 500;
}
footer {
  position: relative;
}
.floating-action-button,
.photo-booth-countdown {
  position: absolute;
  top: -28px;
  left: calc(50% - 28px);
}
.photo-booth-caption-form {
  padding: 0px 8px;
  width: calc(50% - 28px);
}
input {
  margin-bottom: 0;
  box-shadow: none;
  border-bottom: none;

  &:focus {
    box-shadow: none;
  }
}
.photo-booth-video-chooser .small-caption-type {
  padding: 0 8px;
}
select {
  max-width: 232px;
}
</style>
